import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import WarningBanner from '@/components/WarningBanner'
import {
  getExternalCredentials,
  putExternalCredentials,
  type ExternalCredentialResponse,
} from '@/services/accounting'
import {
  CloverExtensionError,
  requestCloverTokensFromExtension,
  type CloverKeepaliveStatus,
} from '@/services/cloverExtension'
import type { BackgroundJobStatus } from '@/services/jobs'
import {
  buildCloverPutPayload,
  CLOVER_SESSION_EXPIRED_BANNER,
  extensionMissingMessage,
  formatKeepaliveTimestamp,
  hostLoginSummary,
  noCloverTabMessage,
} from '@/pages/clover/cloverTokenSync'
import {
  HomebaseExtensionError,
  requestHomebaseTokensFromExtension,
} from '@/services/homebaseExtension'

import {
  BrightSitesExtensionError,
  requestBrightSitesTokensFromExtension,
} from '@/services/brightsitesExtension'
import { useAuth } from '@/contexts/AuthContext'
import { canView } from '@/utils/pagePermissions'
import { toast } from '@/store/toastStore'

type ServiceName = 'clover' | 'homebase' | 'brightsites'

const SERVICE_LABELS: Record<ServiceName, string> = {
  clover: 'Clover',
  homebase: 'Homebase',
  brightsites: 'Bright Sites',
}

/** Anchor within Settings → Accounting Tools where each service's manual token fields live. */
const SERVICE_SETTINGS_ANCHORS: Record<ServiceName, string> = {
  clover: 'data-source-connections',
  homebase: 'homebase-connection',
  brightsites: 'data-source-connections',
}

type GrabState =
  | 'idle'
  | 'extracting'
  | 'saving'
  | 'login_required'
  | 'not_installed'
  | 'error'
  | 'success'

type SecondaryAction = {
  label: string
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  title?: string
}

type Props = {
  service: ServiceName
  className?: string
  /** When set, overrides the default link to the service's Settings section. */
  settingsHref?: string
  /** When false, hides the link to manual token fields in Settings. */
  showSettingsLink?: boolean
  /** When false, hides the last-updated timestamp. */
  showLastUpdated?: boolean
  /** Optional secondary action shown beside the grab-tokens button. */
  secondaryAction?: SecondaryAction
  /** Called after tokens are saved via the extension grab flow. */
  onCredentialsSaved?: () => void
  /** Called after a successful grab when the parent should retry (e.g. re-fetch reports). */
  onGrabSuccess?: () => void
  /** Highlights the card when the external session failed and tokens need refreshing. */
  authRecoveryHint?: boolean
  /** Active Clover backfill job status (clover only). */
  jobStatus?: BackgroundJobStatus | null
  /** Clover extension installed state from useCloverJobSession (clover only). */
  cloverExtensionInstalled?: boolean | null
  /** Clover keepalive status from useCloverJobSession (clover only). */
  cloverKeepaliveStatus?: CloverKeepaliveStatus | null
  /** Clover keepalive toggle in flight (clover only). */
  cloverKeepaliveBusy?: boolean
  /** Clover keepalive checkbox handler (clover only). */
  onCloverKeepaliveToggle?: (enabled: boolean) => void
  /** Sync hook last-pushed ref after a manual grab (clover only). */
  onCloverTokensPushed?: (payload: ReturnType<typeof buildCloverPutPayload>) => void
}

function formatTimestamp(value?: string | null): string | null {
  if (!value) return null
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return value
  }
}

const GIFTCARDS_GRAB_SUFFIX =
  ' Open giftcards.clover.com Reports, reload Clover Token Extractor 1.2.2 at chrome://extensions, refresh this page, then grab again.'

function formatMissingGiftcardsGrabMessage(
  count?: number,
  names?: string[]
): string {
  const cookieCount = count ?? 0
  if (cookieCount === 0) {
    return `Gift cards cookies missing (0 cookies visible on giftcards.clover.com).${GIFTCARDS_GRAB_SUFFIX}`
  }
  const visibleNames = (names ?? []).filter(Boolean).slice(0, 20)
  const detail =
    visibleNames.length > 0
      ? `saw ${visibleNames.join(', ')}; no PLAY_SESSION/AUTH-TOKEN`
      : 'no PLAY_SESSION/AUTH-TOKEN found'
  return `Gift cards cookies missing (${detail}).${GIFTCARDS_GRAB_SUFFIX}`
}

function giftcardsKeysStored(keys: string[] | undefined): boolean {
  return (
    (keys?.includes('giftcardsPlaySession') ?? false) &&
    (keys?.includes('giftcardsAuthToken') ?? false)
  )
}

function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
  className = '',
}: {
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  disabled?: boolean
  className?: string
}) {
  return (
    <div
      className={`inline-flex rounded-lg bg-gray-200 p-0.5 ${className}`}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
            value === opt.value
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

/** Shows Clover or Homebase session credential status with a one-click extension grab flow. */
export default function CredentialStatusCard({
  service,
  className = '',
  settingsHref,
  showSettingsLink = true,
  showLastUpdated = true,
  secondaryAction,
  onCredentialsSaved,
  onGrabSuccess,
  authRecoveryHint = false,
  jobStatus = null,
  cloverExtensionInstalled = null,
  cloverKeepaliveStatus = null,
  cloverKeepaliveBusy = false,
  onCloverKeepaliveToggle,
  onCloverTokensPushed,
}: Props) {
  const { user } = useAuth()
  const showSettings = showSettingsLink && canView(user, 'settings')
  const [status, setStatus] = useState<ExternalCredentialResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [grabState, setGrabState] = useState<GrabState>('idle')
  const [grabMessage, setGrabMessage] = useState<string | null>(null)

  async function load() {
    try {
      setLoading(true)
      setError(null)
      const data = await getExternalCredentials(service)
      setStatus(data)
    } catch (err) {
      console.error(`Failed to load ${service} credentials:`, err)
      setError('Could not check session status')
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [service])

  async function handleGrabTokens() {
    try {
      setGrabState('extracting')
      setGrabMessage(`Extracting tokens from ${label}…`)

      if (service === 'clover') {
        const tokens = await requestCloverTokensFromExtension()

        setGrabState('saving')
        setGrabMessage('Saving Clover tokens…')

        const putPayload = buildCloverPutPayload(tokens)
        await putExternalCredentials('clover', putPayload)
        onCloverTokensPushed?.(putPayload)

        await load()

        const hasGiftcardsCookies = Boolean(
          tokens.giftcardsPlaySession && tokens.giftcardsAuthToken
        )
        const baseMessage =
          tokens.merchantId && tokens.merchantId !== 'UNKNOWN'
            ? `Clover tokens saved (merchant ${tokens.merchantId}).`
            : 'Clover tokens saved.'
        const giftcardsMessage = hasGiftcardsCookies
          ? ' Gift cards PLAY_SESSION + AUTH-TOKEN saved.'
          : ` ${formatMissingGiftcardsGrabMessage(
              tokens.giftcardsCookieCount,
              tokens.giftcardsCookieNames
            )}`
        const fullMessage = `${baseMessage}${giftcardsMessage}`

        setGrabState('success')
        setGrabMessage(fullMessage)
        if (hasGiftcardsCookies) {
          toast.success(fullMessage)
        } else {
          toast.warning(fullMessage)
        }
        onCredentialsSaved?.()
      } else if (service === 'homebase') {
        const tokens = await requestHomebaseTokensFromExtension()

        setGrabState('saving')
        setGrabMessage('Saving Homebase tokens…')

        await putExternalCredentials('homebase', {
          session_id: tokens.sessionId,
          stytch_session_jwt: tokens.stytchSessionJwt,
        })

        await load()
        onCredentialsSaved?.()
        setGrabState('success')
        setGrabMessage('Homebase tokens saved.')
      } else {
        const tokens = await requestBrightSitesTokensFromExtension()

        setGrabState('saving')
        setGrabMessage('Saving Bright Sites tokens…')

        await putExternalCredentials('brightsites', {
          jwt: tokens.jwt,
          cookie: tokens.cookie,
        })

        await load()
        onCredentialsSaved?.()
        setGrabState('success')
        setGrabMessage('Bright Sites tokens saved.')
      }

      onGrabSuccess?.()
    } catch (err) {
      if (
        err instanceof CloverExtensionError ||
        err instanceof HomebaseExtensionError ||
        err instanceof BrightSitesExtensionError
      ) {
        if (err.code === 'NOT_INSTALLED') {
          setGrabState('not_installed')
          setGrabMessage(err.message)
          return
        }
        if (err.code === 'LOGIN_REQUIRED') {
          setGrabState('login_required')
          if (service === 'homebase') {
            setGrabMessage(`${err.message} If you're already logged in, click Retry.`)
          } else {
            setGrabMessage(`We opened ${label} in a new tab. Log in, then click Retry.`)
          }
          return
        }
        setGrabState('error')
        setGrabMessage(err.message)
        return
      }

      setGrabState('error')
      setGrabMessage(err instanceof Error ? err.message : `Failed to save ${label} tokens`)
    }
  }

  const label = SERVICE_LABELS[service]
  const resolvedSettingsHref = settingsHref ?? `/settings#${SERVICE_SETTINGS_ANCHORS[service]}`
  const configured = status?.configured ?? false
  const updatedAt = formatTimestamp(status?.updated_at)
  const verifiedAt = formatTimestamp(status?.last_verified_at)
  const grabbing = grabState === 'extracting' || grabState === 'saving'
  const waitingForTokens = service === 'clover' && jobStatus === 'waiting_for_clover_tokens'
  const keepaliveOn = cloverKeepaliveStatus?.enabled ?? true
  const lastTick = formatKeepaliveTimestamp(cloverKeepaliveStatus?.lastTickAt)
  const grabButtonLabel =
    grabState === 'saving'
      ? 'Saving…'
      : grabState === 'extracting'
        ? 'Extracting…'
        : grabState === 'login_required'
          ? 'Retry'
          : service === 'clover'
            ? 'Fetch'
            : `Fetch ${label} tokens`

  return (
    <div
      className={`rounded-xl border bg-white p-4 shadow-sm shadow-gray-950/[0.03] ${
        authRecoveryHint ? 'border-amber-300 ring-1 ring-amber-100' : 'border-gray-200'
      } ${className}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="container-title">{label} session</h3>
        {loading ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-100">
            <ArrowPathIcon className="h-3 w-3 animate-spin" />
            Checking…
          </span>
        ) : configured ? null : (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-amber-100">
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            Not configured
          </span>
        )}
      </div>
      {service !== 'clover' ? (
        <p className="mt-1 text-xs leading-relaxed text-gray-500">
          {configured
            ? `Grab fresh tokens when your ${label} session expires.`
            : `Grab tokens from a logged-in ${label} tab before fetching data.`}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-end gap-3">
        {service === 'clover' ? (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">Clover Tokens</span>
            <button
              type="button"
              className="btn btn-primary shrink-0 text-sm"
              onClick={() => void handleGrabTokens()}
              disabled={grabbing}
            >
              {grabbing ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : null}
              {grabButtonLabel}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-primary shrink-0 text-sm"
            onClick={() => void handleGrabTokens()}
            disabled={grabbing}
          >
            {grabbing ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : null}
            {grabButtonLabel}
          </button>
        )}
        {service === 'clover' && cloverExtensionInstalled === true ? (
          <div className="credential-keepalive-row flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">Clover Keepalive</span>
            <SegmentedToggle
              ariaLabel="Clover Keepalive"
              className="credential-keepalive-toggle"
              value={keepaliveOn ? 'on' : 'off'}
              disabled={cloverKeepaliveBusy}
              onChange={(value) => onCloverKeepaliveToggle?.(value === 'on')}
              options={[
                { value: 'off', label: 'Off' },
                { value: 'on', label: 'On' },
              ]}
            />
          </div>
        ) : null}
        {secondaryAction ? (
          <button
            type="button"
            className="btn border border-gray-200 bg-gray-50 text-sm text-gray-600 shadow-sm hover:bg-gray-100 hover:text-gray-700"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled || secondaryAction.loading}
            title={secondaryAction.title}
          >
            {secondaryAction.loading ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : null}
            {secondaryAction.label}
          </button>
        ) : showSettings ? (
          <Link
            to={resolvedSettingsHref}
            className="btn border border-gray-200 bg-gray-50 text-sm text-gray-600 shadow-sm hover:bg-gray-100 hover:text-gray-700"
          >
            Manage in Settings
          </Link>
        ) : null}
      </div>
      <div className="mt-3 space-y-1">
        {showLastUpdated && updatedAt ? (
          <p className="text-xs text-gray-500">Last updated {updatedAt}</p>
        ) : null}
        {verifiedAt ? <p className="text-xs text-gray-500">Last verified {verifiedAt}</p> : null}
        {service === 'clover' && !loading ? (
          <p className="text-xs text-gray-500">
            Gift cards PLAY_SESSION / AUTH-TOKEN:{' '}
            {giftcardsKeysStored(status?.keys) ? 'stored' : 'not stored'}
          </p>
        ) : null}
        {service === 'clover' && cloverExtensionInstalled === true && cloverKeepaliveStatus?.hosts ? (
          <p className="text-xs text-gray-500">{hostLoginSummary(cloverKeepaliveStatus.hosts)}</p>
        ) : null}
        {service === 'clover' &&
        cloverExtensionInstalled === true &&
        !cloverKeepaliveStatus?.noTab &&
        lastTick ? (
          <p className="text-xs text-gray-500">Last tick {lastTick}</p>
        ) : null}
      </div>
      {authRecoveryHint ? (
        <p className="mt-2 text-xs font-medium text-amber-800">
          {label} session expired — grab fresh tokens, then retry your fetch.
        </p>
      ) : null}
      {service === 'clover' && cloverExtensionInstalled === false ? (
        <p className="mt-2 text-sm text-gray-600">{extensionMissingMessage()}</p>
      ) : null}
      {waitingForTokens ? (
        <WarningBanner className="mt-3" message={CLOVER_SESSION_EXPIRED_BANNER} />
      ) : null}
      {service === 'clover' && cloverExtensionInstalled === true && cloverKeepaliveStatus?.noTab ? (
        <p className="mt-2 text-xs text-gray-500">{noCloverTabMessage()}</p>
      ) : null}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      {grabMessage ? (
        <p
          className={`mt-2 text-xs ${
            grabState === 'success'
              ? 'text-emerald-700'
              : grabState === 'error' || grabState === 'not_installed'
                ? 'text-red-600'
                : grabState === 'login_required'
                  ? 'text-amber-800'
                  : 'text-gray-500'
          }`}
        >
          {grabMessage}
        </p>
      ) : null}
    </div>
  )
}
